import { useState, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, Search, Filter, ListTree, Trash2, Edit3, X } from 'lucide-react';
import { useRequirements, useCreateRequirement, useDeleteRequirement } from '../hooks/useRequirements';
import { REQUIREMENT_STATUS_LABELS, REQUIREMENT_CATEGORY_LABELS, PRIORITY_LABELS } from '@maios/shared';
import toast from 'react-hot-toast';
import Spinner from '../components/ui/Spinner';
import type { ProjectDetail } from '../services/projectService';
import type { RequirementListItem } from '../services/requirementService';
import ErrorBoundary from '../components/ui/ErrorBoundary';
import SingleSelectDropdown from '../components/common/SingleSelectDropdown';
import axios from 'axios';
import { usePanelContext } from '../contexts/PanelContext';

// --- 배지 색상 ---
const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  REVIEW: 'bg-yellow-100 text-yellow-800',
  APPROVED: 'bg-green-100 text-green-800',
  DEPRECATED: 'bg-red-100 text-red-700',
};

const PRIORITY_COLOR: Record<string, string> = {
  BLOCKER: 'bg-black text-white',
  CRITICAL: 'bg-red-600 text-white',
  HIGH: 'bg-red-100 text-red-700',
  MEDIUM: 'bg-yellow-100 text-yellow-700',
  LOW: 'bg-blue-100 text-blue-700',
};

const CATEGORY_COLOR: Record<string, string> = {
  FUNCTIONAL: 'bg-indigo-100 text-indigo-700',
  NON_FUNCTIONAL: 'bg-purple-100 text-purple-700',
  CONSTRAINT: 'bg-orange-100 text-orange-700',
};

export default function RequirementListPage() {
  const { project } = useOutletContext<{ project: ProjectDetail }>();
  const { data: requirements, isLoading } = useRequirements(project.id);
  const createMutation = useCreateRequirement(project.id);
  const deleteMutation = useDeleteRequirement(project.id);

  // 필터
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  // 트리 열림/닫힘
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // 생성 모달
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createParentId, setCreateParentId] = useState<string | undefined>(undefined);

  // CHG-028: 사이드 패널 (PanelContext 기반)
  const { openDetailPanel } = usePanelContext();

  // 삭제 불가 경고 (CHG-003)
  const [deleteBlockMsg, setDeleteBlockMsg] = useState<string | null>(null);

  // 전체 요구사항을 flat 리스트로 변환 (필터/검색 적용)
  const flattenWithFilter = useCallback(
    (items: RequirementListItem[], depth: number): Array<RequirementListItem & { depth: number }> => {
      const result: Array<RequirementListItem & { depth: number }> = [];
      for (const item of items) {
        const matchSearch =
          !searchQuery.trim() ||
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.reqId.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.description ?? '').toLowerCase().includes(searchQuery.toLowerCase());

        const matchStatus = !statusFilter || item.status === statusFilter;
        const matchCategory = !categoryFilter || item.category === categoryFilter;
        const matchPriority = !priorityFilter || item.priority === priorityFilter;

        const childResults =
          item.children && item.children.length > 0
            ? flattenWithFilter(item.children as RequirementListItem[], depth + 1)
            : [];

        // 자신이 매치하거나 자식 중 매치하는 게 있으면 포함
        if ((matchSearch && matchStatus && matchCategory && matchPriority) || childResults.length > 0) {
          result.push({ ...item, depth });
          if (expandedIds.has(item.id) || searchQuery.trim()) {
            result.push(...childResults);
          }
        }
      }
      return result;
    },
    [searchQuery, statusFilter, categoryFilter, priorityFilter, expandedIds],
  );

  const flatList = useMemo(() => {
    if (!requirements) return [];
    return flattenWithFilter(requirements, 0);
  }, [requirements, flattenWithFilter]);

  // 통계
  const stats = useMemo(() => {
    if (!requirements) return { total: 0, draft: 0, review: 0, approved: 0, deprecated: 0 };
    const count = (items: RequirementListItem[]): Record<string, number> => {
      let total = 0, draft = 0, review = 0, approved = 0, deprecated = 0;
      for (const item of items) {
        total++;
        if (item.status === 'DRAFT') draft++;
        else if (item.status === 'REVIEW') review++;
        else if (item.status === 'APPROVED') approved++;
        else if (item.status === 'DEPRECATED') deprecated++;
        if (item.children) {
          const childCounts = count(item.children as RequirementListItem[]);
          total += childCounts.total;
          draft += childCounts.draft;
          review += childCounts.review;
          approved += childCounts.approved;
          deprecated += childCounts.deprecated;
        }
      }
      return { total, draft, review, approved, deprecated };
    };
    return count(requirements);
  }, [requirements]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // CHG-003: 삭제 핸들러 - 409 에러 분기 처리
  const handleDelete = async (reqId: string, title: string) => {
    if (!confirm(`"${title}" 요구사항을 삭제하시겠습니까?`)) return;
    try {
      await deleteMutation.mutateAsync(reqId);
      toast.success('요구사항이 삭제되었습니다.');
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setDeleteBlockMsg(err.response.data?.error ?? '연결된 명세가 존재하여 삭제할 수 없습니다.');
      } else {
        toast.error('삭제에 실패했습니다.');
      }
    }
  };

  // CHG-001: 생성 핸들러 - description 포함
  const handleCreate = async (data: {
    title: string;
    description?: string;
    category: string;
    priority: string;
    parentId?: string;
    assigneeId?: string;
  }) => {
    try {
      await createMutation.mutateAsync(data);
      setShowCreateForm(false);
      setCreateParentId(undefined);
      toast.success('요구사항이 생성되었습니다.');
    } catch {
      toast.error('생성에 실패했습니다.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <ErrorBoundary fallbackMessage="요구사항 목록을 표시하는 중 오류가 발생했습니다.">
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900">요구사항</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            총 {stats.total}개 · 초안 {stats.draft} · 검토 {stats.review} · 승인 {stats.approved} · 폐기 {stats.deprecated}
          </p>
        </div>
        <button
          onClick={() => { setCreateParentId(undefined); setShowCreateForm(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors shadow-sm"
        >
          <Plus className="size-4" />요구사항 추가
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="ID, 제목, 설명 검색..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Filter className="size-4 text-gray-400" />
          <SingleSelectDropdown
            label="전체 카테고리"
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={Object.entries(REQUIREMENT_CATEGORY_LABELS).map(([key, label]) => ({ value: key, label }))}
          />
          <SingleSelectDropdown
            label="전체 상태"
            value={statusFilter}
            onChange={setStatusFilter}
            options={Object.entries(REQUIREMENT_STATUS_LABELS).map(([key, label]) => ({ value: key, label }))}
          />
          <SingleSelectDropdown
            label="전체 우선순위"
            value={priorityFilter}
            onChange={setPriorityFilter}
            options={Object.entries(PRIORITY_LABELS).map(([key, label]) => ({ value: key, label }))}
          />
        </div>
      </div>

      {/* Table */}
      {flatList.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">ID</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">제목</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">카테고리</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">우선순위</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">상태</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">담당자</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 whitespace-nowrap">명세</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 whitespace-nowrap">작업</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600 whitespace-nowrap">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {flatList.map((req) => (
                <tr key={req.id} className="hover:bg-gray-50 transition-colors group">
                  {/* ID */}
                  <td className="px-4 py-3">
                    <div className="flex items-center" style={{ paddingLeft: `${req.depth * 24}px` }}>
                      {(req._count?.children ?? 0) > 0 ? (
                        <button
                          onClick={() => toggleExpanded(req.id)}
                          className="p-0.5 mr-1 text-gray-400 hover:text-gray-600 rounded text-sm leading-none"
                        >
                          {expandedIds.has(req.id) || searchQuery.trim() ? '▼' : '▶'}
                        </button>
                      ) : (
                        <span className="w-5 inline-block" />
                      )}
                      <span className="font-mono text-sm text-primary-600 font-medium">{req.reqId}</span>
                    </div>
                  </td>

                  {/* 제목 — REQ-V28-003: 클릭 시 사이드 패널 열기 */}
                  <td className="px-4 py-3">
                    <span
                      className="font-medium text-gray-900 cursor-pointer hover:text-blue-600 hover:underline"
                      onClick={() => openDetailPanel('requirement', req.id)}
                    >
                      {req.title}
                    </span>
                    {req.version > 1 && (
                      <span className="ml-1.5 text-[11px] text-gray-400">v{req.version}</span>
                    )}
                  </td>

                  {/* 카테고리 */}
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${CATEGORY_COLOR[req.category] ?? 'bg-gray-100 text-gray-700'}`}>
                      {REQUIREMENT_CATEGORY_LABELS[req.category] ?? req.category}
                    </span>
                  </td>

                  {/* 우선순위 */}
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${PRIORITY_COLOR[req.priority] ?? 'bg-gray-100 text-gray-700'}`}>
                      {PRIORITY_LABELS[req.priority] ?? req.priority}
                    </span>
                  </td>

                  {/* 상태 */}
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${STATUS_COLOR[req.status] ?? 'bg-gray-100 text-gray-700'}`}>
                      {REQUIREMENT_STATUS_LABELS[req.status] ?? req.status}
                    </span>
                  </td>

                  {/* 담당자 */}
                  <td className="px-4 py-3">
                    {req.assignee ? (
                      <span className="text-sm text-gray-700">{req.assignee.name}</span>
                    ) : (
                      <span className="text-sm text-gray-400">미할당</span>
                    )}
                  </td>

                  {/* 명세 수 */}
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm text-gray-500">{req._count?.specifications ?? 0}</span>
                  </td>

                  {/* 이슈 수 */}
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm text-gray-500">{req._count?.linkedIssues ?? 0}</span>
                  </td>

                  {/* 작업 */}
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {/* depth 5(0-indexed: 4) 미만일 때만 하위 추가 표시 */}
                      {req.depth < 4 && (
                        <button
                          onClick={() => { setCreateParentId(req.id); setShowCreateForm(true); }}
                          title="하위 요구사항 추가"
                          className="p-1 text-gray-400 hover:text-primary-600 rounded"
                        >
                          <Plus className="size-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => openDetailPanel('requirement', req.id)}
                        title="편집"
                        className="p-1 text-gray-400 hover:text-primary-600 rounded"
                      >
                        <Edit3 className="size-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(req.id, req.title)}
                        title="삭제"
                        className="p-1 text-gray-400 hover:text-red-500 rounded"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-48 bg-white border border-gray-200 rounded-xl">
          <ListTree className="size-10 text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">요구사항이 없습니다.</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            첫 번째 요구사항 작성하기
          </button>
        </div>
      )}

      {/* 생성 모달 (CHG-001) */}
      {showCreateForm && (
        <CreateRequirementModal
          parentId={createParentId}
          members={project.members}
          onClose={() => { setShowCreateForm(false); setCreateParentId(undefined); }}
          onSubmit={handleCreate}
          isLoading={createMutation.isPending}
        />
      )}

      {/* 삭제 불가 경고 팝업 (CHG-003) */}
      {deleteBlockMsg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6 text-center">
            <div className="text-amber-500 text-3xl mb-3">⚠️</div>
            <p className="text-sm text-gray-700 mb-5">{deleteBlockMsg}</p>
            <button
              onClick={() => setDeleteBlockMsg(null)}
              className="px-6 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              확인
            </button>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
}

// --- 생성 모달 (CHG-001: description textarea 추가) ---
function CreateRequirementModal({
  parentId,
  members,
  onClose,
  onSubmit,
  isLoading,
}: {
  parentId?: string;
  members: Array<{ user: { id: string; name: string } }>;
  onClose: () => void;
  onSubmit: (data: { title: string; description?: string; category: string; priority: string; parentId?: string; assigneeId?: string }) => void;
  isLoading: boolean;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('FUNCTIONAL');
  const [priority, setPriority] = useState('MEDIUM');
  const [assigneeId, setAssigneeId] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      description: description.trim() || undefined,
      category,
      priority,
      parentId,
      assigneeId: assigneeId || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            {parentId ? '하위 요구사항 추가' : '새 요구사항 추가'}
          </h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="size-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">제목 *</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="요구사항 제목을 입력하세요"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              autoFocus
            />
          </div>
          {/* CHG-001: 내용 textarea */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">내용</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="요구사항에 대한 상세 내용을 입력하세요"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-y"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">카테고리</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
              >
                {Object.entries(REQUIREMENT_CATEGORY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">우선순위</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
              >
                {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">담당자</label>
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
            >
              <option value="">미할당</option>
              {members.map((m) => (
                <option key={m.user.id} value={m.user.id}>{m.user.name}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={!title.trim() || isLoading}
              className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? '생성 중...' : '생성'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

